package com.yendo.mx.ui.profile

import com.yendo.mx.utils.BackHandler
import androidx.compose.foundation.Image
import androidx.compose.foundation.background
import androidx.compose.foundation.clickable
import androidx.compose.foundation.isSystemInDarkTheme
import androidx.compose.foundation.layout.*
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.lazy.items
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.automirrored.filled.ArrowBack
import androidx.compose.material.icons.filled.*
import androidx.compose.material3.*
import androidx.compose.runtime.*
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.AnnotatedString
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.input.OffsetMapping
import androidx.compose.ui.text.input.TransformedText
import androidx.compose.ui.text.input.VisualTransformation
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp

import com.yendo.mx.repository.AuthRepository
import com.yendo.mx.repository.MercadoPagoRepository // 🚀 IMPORTAMOS EL REPOSITORIO DE MP
import com.yendo.mx.ui.theme.RosaYendo
import dev.gitlive.firebase.Firebase
import dev.gitlive.firebase.auth.auth
import dev.gitlive.firebase.firestore.firestore
import kotlinx.coroutines.flow.catch
import kotlinx.coroutines.delay
import kotlinx.coroutines.launch
import kotlinx.serialization.Serializable

// 🚀 IMPORTACIONES DE LOTTIE
import org.jetbrains.compose.resources.ExperimentalResourceApi
import yendo.shared.generated.resources.*
import io.github.alexzhirkevich.compottie.Compottie
import io.github.alexzhirkevich.compottie.LottieCompositionSpec
import io.github.alexzhirkevich.compottie.rememberLottieComposition
import io.github.alexzhirkevich.compottie.rememberLottiePainter

// 🚀 MODELO PREPARADO PARA FIREBASE
@Serializable
data class TarjetaGuardada(
    val id: String = "",
    val terminacion: String = "",
    val titular: String = "",
    val marca: String = "",
    val tokenPago: String = "", // Aquí guardaremos el ID que nos dé Mercado Pago
    val issuerId: String = "" // 🚀 NUEVO
)

// ALGORITMO DE LUHN
fun esTarjetaValidaLuhn(numero: String): Boolean {
    if (numero.length < 13) return false
    var suma = 0
    var alternar = false
    for (i in numero.length - 1 downTo 0) {
        var n = numero[i].toString().toInt()
        if (alternar) {
            n *= 2
            if (n > 9) n -= 9
        }
        suma += n
        alternar = !alternar
    }
    return suma % 10 == 0
}

@OptIn(ExperimentalMaterial3Api::class, ExperimentalResourceApi::class)
@Composable
fun MisTarjetasScreen(onBack: () -> Unit) {
    val isDark = isSystemInDarkTheme()
    val colorFondoBase = if (isDark) Color(0xFF121212) else Color(0xFFF8F8F8)
    val colorSuperficie = if (isDark) Color(0xFF1E1E1E) else Color.White
    val colorTextoPrimario = if (isDark) Color.White else Color.Black
    val colorTextoSecundario = if (isDark) Color(0xFFAAAAAA) else Color.Gray

    val scope = rememberCoroutineScope()
    val usuarioId = AuthRepository.usuarioActual.collectAsState().value?.uid

    BackHandler { onBack() }

    val listaTarjetas = remember { mutableStateListOf<TarjetaGuardada>() }
    var mostrarDialogoAgregar by remember { mutableStateOf(false) }
    var isLoading by remember { mutableStateOf(true) }

    val composition by rememberLottieComposition {
        LottieCompositionSpec.JsonString(Res.readBytes("files/yendo_cargando.json").decodeToString())
    }

    LaunchedEffect(usuarioId) {
        if (usuarioId != null) {
            try {
                Firebase.firestore
                    .collection("Usuarios")
                    .document(usuarioId)
                    .collection("Tarjetas")
                    .snapshots
                    .catch { println("YENDO_ERROR: Error en flujo de tarjetas - ${it.message}"); isLoading = false }
                    .collect { snapshot ->
                        val tarjetasDb = snapshot.documents.map { doc ->
                            doc.data<TarjetaGuardada>().copy(id = doc.id)
                        }
                        listaTarjetas.clear()
                        listaTarjetas.addAll(tarjetasDb)
                        isLoading = false
                    }
            } catch (e: Exception) {
                // Si la excepción no es CancellationException, la imprimimos
                println("YENDO_ERROR: Falló al cargar tarjetas - ${e.message}")
                isLoading = false
            }
        } else {
            isLoading = false
        }
    }

    Scaffold(
        topBar = {
            TopAppBar(
                title = { Text("Mis Tarjetas", fontWeight = FontWeight.ExtraBold, color = colorTextoPrimario) },
                navigationIcon = {
                    IconButton(onClick = onBack) {
                        Icon(Icons.AutoMirrored.Filled.ArrowBack, contentDescription = "Atrás", tint = colorTextoPrimario)
                    }
                },
                colors = TopAppBarDefaults.topAppBarColors(containerColor = colorSuperficie)
            )
        },
        floatingActionButton = {
            if (!isLoading) {
                ExtendedFloatingActionButton(
                    onClick = { mostrarDialogoAgregar = true },
                    containerColor = RosaYendo,
                    contentColor = Color.White,
                    shape = RoundedCornerShape(16.dp)
                ) {
                    Icon(Icons.Default.AddCard, contentDescription = null)
                    Spacer(Modifier.width(8.dp))
                    Text("Agregar Tarjeta", fontWeight = FontWeight.Bold)
                }
            }
        },
        containerColor = colorFondoBase
    ) { padding ->

        if (isLoading) {
            Box(modifier = Modifier.fillMaxSize().padding(padding), contentAlignment = Alignment.Center) {
                val painter = rememberLottiePainter(composition = composition, iterations = Compottie.IterateForever)
                Image(painter = painter, contentDescription = "Cargando...", modifier = Modifier.size(150.dp))
            }
        } else if (listaTarjetas.isEmpty()) {
            Column(
                modifier = Modifier.fillMaxSize().padding(padding),
                verticalArrangement = Arrangement.Center,
                horizontalAlignment = Alignment.CenterHorizontally
            ) {
                Icon(Icons.Default.CreditCardOff, null, tint = colorTextoSecundario.copy(alpha = 0.5f), modifier = Modifier.size(80.dp))
                Spacer(Modifier.height(16.dp))
                Text("No tienes tarjetas guardadas", fontWeight = FontWeight.Bold, fontSize = 18.sp, color = colorTextoPrimario)
                Text("Tus pagos en Yendo Mx son 100% seguros", color = colorTextoSecundario, fontSize = 14.sp)
            }
        } else {
            LazyColumn(
                modifier = Modifier.fillMaxSize().padding(padding),
                contentPadding = PaddingValues(top = 16.dp, bottom = 100.dp, start = 16.dp, end = 16.dp),
                verticalArrangement = Arrangement.spacedBy(16.dp)
            ) {
                items(listaTarjetas, key = { it.id }) { tarjeta ->
                    TarjetaItem(
                        tarjeta = tarjeta,
                        isDark = isDark,
                        onEliminar = {
                            scope.launch {
                                try {
                                    val uid = usuarioId ?: return@launch
                                    Firebase.firestore
                                        .collection("Usuarios")
                                        .document(uid)
                                        .collection("Tarjetas")
                                        .document(tarjeta.id)
                                        .delete()
                                } catch (e: Exception) {
                                    println("YENDO_ERROR: Falló al eliminar - ${e.message}")
                                }
                            }
                        }
                    )
                }
            }
        }

        if (mostrarDialogoAgregar) {
            DialogoNuevaTarjeta(
                isDark = isDark,
                onDismiss = { mostrarDialogoAgregar = false },
                onGuardar = { nuevaTarjeta ->
                    scope.launch {
                        try {
                            val uid = usuarioId ?: return@launch
                            val docRef = Firebase.firestore
                                .collection("Usuarios")
                                .document(uid)
                                .collection("Tarjetas")
                                .document

                            docRef.set(nuevaTarjeta.copy(id = docRef.id))
                            mostrarDialogoAgregar = false
                        } catch (e: Exception) {
                            println("YENDO_ERROR: Falló al guardar - ${e.message}")
                        }
                    }
                }
            )
        }
    }
}

@Composable
fun TarjetaItem(tarjeta: TarjetaGuardada, isDark: Boolean, onEliminar: () -> Unit) {
    val colorSuperficie = if (isDark) Color(0xFF1E1E1E) else Color.White
    val colorTextoPrimario = if (isDark) Color.White else Color.Black
    val colorTextoSecundario = if (isDark) Color(0xFFAAAAAA) else Color.Gray

    val (marcaColor, marcaBg) = when(tarjeta.marca.lowercase()) {
        "visa" -> Pair(Color.White, Color(0xFF1A237E))
        "mastercard" -> Pair(Color.White, Color(0xFFB71C1C))
        "amex" -> Pair(Color.White, Color(0xFF00695C))
        else -> Pair(Color.White, Color.DarkGray)
    }

    var mostrarConfirmacion by remember { mutableStateOf(false) }

    Card(
        modifier = Modifier.fillMaxWidth(),
        colors = CardDefaults.cardColors(containerColor = colorSuperficie),
        elevation = CardDefaults.cardElevation(defaultElevation = 2.dp),
        shape = RoundedCornerShape(16.dp)
    ) {
        Column(modifier = Modifier.padding(20.dp)) {
            Row(modifier = Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.SpaceBetween, verticalAlignment = Alignment.CenterVertically) {
                Box(
                    modifier = Modifier.clip(RoundedCornerShape(4.dp)).background(marcaBg).padding(horizontal = 8.dp, vertical = 4.dp)
                ) {
                    Text(tarjeta.marca, color = marcaColor, fontSize = 10.sp, fontWeight = FontWeight.Black)
                }

                IconButton(onClick = { mostrarConfirmacion = true }, modifier = Modifier.size(24.dp)) {
                    Icon(Icons.Default.DeleteOutline, "Eliminar", tint = colorTextoSecundario)
                }
            }

            Spacer(Modifier.height(16.dp))
            Text(
                text = "**** **** **** ${tarjeta.terminacion}",
                color = colorTextoPrimario,
                fontWeight = FontWeight.Bold,
                fontSize = 20.sp,
                letterSpacing = 2.sp
            )

            Spacer(Modifier.height(12.dp))
            Text(
                text = tarjeta.titular.uppercase(),
                color = colorTextoSecundario,
                fontSize = 12.sp,
                fontWeight = FontWeight.Medium
            )
        }
    }

    if (mostrarConfirmacion) {
        AlertDialog(
            containerColor = colorSuperficie,
            onDismissRequest = { mostrarConfirmacion = false },
            title = { Text("Eliminar Tarjeta", color = Color.Red, fontWeight = FontWeight.Bold) },
            text = { Text("¿Estás seguro de eliminar esta tarjeta terminada en ${tarjeta.terminacion}?", color = colorTextoSecundario) },
            confirmButton = {
                Button(
                    onClick = {
                        mostrarConfirmacion = false
                        onEliminar()
                    },
                    colors = ButtonDefaults.buttonColors(Color.Red)
                ) { Text("Eliminar", color = Color.White) }
            },
            dismissButton = {
                TextButton(onClick = { mostrarConfirmacion = false }) { Text("Cancelar", color = colorTextoSecundario) }
            }
        )
    }
}

@OptIn(ExperimentalResourceApi::class)
@Composable
fun DialogoNuevaTarjeta(isDark: Boolean, onDismiss: () -> Unit, onGuardar: (TarjetaGuardada) -> Unit) {
    val colorSuperficie = if (isDark) Color(0xFF1E1E1E) else Color.White
    val colorTextoPrimario = if (isDark) Color.White else Color.Black
    val colorTextoSecundario = if (isDark) Color(0xFFAAAAAA) else Color.Gray
    val colorBorde = if (isDark) Color(0xFF333333) else Color.LightGray

    var numTarjeta by remember { mutableStateOf("") }
    var nombreTitular by remember { mutableStateOf("") }
    var vencimiento by remember { mutableStateOf("") }
    var cvv by remember { mutableStateOf("") }

    var mensajeError by remember { mutableStateOf("") }

    // 🚀 ESTADO PARA BLOQUEAR LA PANTALLA Y MOSTRAR LOTTIE
    var procesando by remember { mutableStateOf(false) }
    val scope = rememberCoroutineScope()

    // 🚀 LOTTIE PARA EL GUARDADO
    val composition by rememberLottieComposition {
        LottieCompositionSpec.JsonString(Res.readBytes("files/yendo_cargando.json").decodeToString())
    }

    val textFieldColors = OutlinedTextFieldDefaults.colors(
        focusedBorderColor = RosaYendo,
        unfocusedBorderColor = colorBorde,
        focusedTextColor = colorTextoPrimario,
        unfocusedTextColor = colorTextoPrimario,
        focusedLabelColor = RosaYendo,
        unfocusedLabelColor = colorTextoSecundario,
        cursorColor = RosaYendo
    )

    AlertDialog(
        onDismissRequest = { if (!procesando) onDismiss() }, // No deja cerrar si está cargando
        containerColor = colorSuperficie,
        title = { Text("Nueva Tarjeta", fontWeight = FontWeight.ExtraBold, color = colorTextoPrimario) },
        text = {
            // 🚀 USAMOS UN BOX PARA PONER EL LOTTIE ENCIMA SI ESTÁ CARGANDO
            Box(contentAlignment = Alignment.Center) {
                Column(verticalArrangement = Arrangement.spacedBy(12.dp)) {
                    Surface(color = Color(0xFF4CAF50).copy(0.1f), shape = RoundedCornerShape(8.dp)) {
                        Row(modifier = Modifier.padding(8.dp), verticalAlignment = Alignment.CenterVertically) {
                            Icon(Icons.Default.Lock, null, tint = Color(0xFF4CAF50), modifier = Modifier.size(16.dp))
                            Spacer(Modifier.width(8.dp))
                            Text("Tus datos están encriptados y no se guardan en nuestros servidores.", fontSize = 10.sp, color = Color(0xFF4CAF50))
                        }
                    }

                    if (mensajeError.isNotEmpty()) {
                        Text(mensajeError, color = Color.Red, fontSize = 12.sp, fontWeight = FontWeight.Bold)
                    }

                    OutlinedTextField(
                        value = numTarjeta,
                        onValueChange = { input ->
                            val purosNumeros = input.filter { it.isDigit() }
                            if (purosNumeros.length <= 16) {
                                numTarjeta = purosNumeros
                                mensajeError = ""
                            }
                        },
                        label = { Text("Número de tarjeta") },
                        isError = mensajeError.contains("inválido"),
                        enabled = !procesando, // Se bloquea si está cargando
                        keyboardOptions = androidx.compose.foundation.text.KeyboardOptions(keyboardType = androidx.compose.ui.text.input.KeyboardType.Number),
                        visualTransformation = FormatoTarjetaVisual(),
                        modifier = Modifier.fillMaxWidth(),
                        shape = RoundedCornerShape(12.dp),
                        colors = textFieldColors
                    )

                    OutlinedTextField(
                        value = nombreTitular,
                        onValueChange = { nombreTitular = it.uppercase(); mensajeError = "" },
                        label = { Text("Nombre del titular") },
                        enabled = !procesando,
                        modifier = Modifier.fillMaxWidth(),
                        shape = RoundedCornerShape(12.dp),
                        colors = textFieldColors
                    )

                    Row(horizontalArrangement = Arrangement.spacedBy(12.dp)) {
                        OutlinedTextField(
                            value = vencimiento,
                            onValueChange = { input ->
                                val purosNumeros = input.filter { it.isDigit() }
                                if (purosNumeros.length <= 4) {
                                    vencimiento = purosNumeros
                                    mensajeError = ""
                                }
                            },
                            label = { Text("MM/AA") },
                            enabled = !procesando,
                            keyboardOptions = androidx.compose.foundation.text.KeyboardOptions(keyboardType = androidx.compose.ui.text.input.KeyboardType.Number),
                            visualTransformation = FormatoFechaVisual(),
                            modifier = Modifier.weight(1f),
                            shape = RoundedCornerShape(12.dp),
                            colors = textFieldColors
                        )

                        OutlinedTextField(
                            value = cvv,
                            onValueChange = { input ->
                                val purosNumeros = input.filter { it.isDigit() }
                                if (purosNumeros.length <= 4) {
                                    cvv = purosNumeros
                                    mensajeError = ""
                                }
                            },
                            label = { Text("CVV") },
                            enabled = !procesando,
                            keyboardOptions = androidx.compose.foundation.text.KeyboardOptions(keyboardType = androidx.compose.ui.text.input.KeyboardType.NumberPassword),
                            modifier = Modifier.weight(1f),
                            shape = RoundedCornerShape(12.dp),
                            colors = textFieldColors
                        )
                    }
                }

                // 🚀 LA MAGIA DEL LOTTIE SOBREPUESTO
                if (procesando) {
                    Box(
                        modifier = Modifier
                            .matchParentSize()
                            .background(colorSuperficie.copy(alpha = 0.8f))
                            // Bloquea clics accidentales atrás
                            .clickable(enabled = false) {},
                        contentAlignment = Alignment.Center
                    ) {
                        val painter = rememberLottiePainter(composition = composition, iterations = Compottie.IterateForever)
                        Image(painter = painter, contentDescription = "Validando...", modifier = Modifier.size(120.dp))
                    }
                }
            }
        },
        confirmButton = {
            Button(
                onClick = {
                    if (numTarjeta.length < 15 || nombreTitular.isEmpty() || vencimiento.length < 4 || cvv.length < 3) {
                        mensajeError = "Por favor completa todos los campos correctamente."
                        return@Button
                    }

                    val mes = vencimiento.substring(0, 2).toIntOrNull() ?: 0
                    if (mes !in 1..12) {
                        mensajeError = "El mes de la tarjeta debe ser entre 01 y 12."
                        return@Button
                    }

                    if (!esTarjetaValidaLuhn(numTarjeta)) {
                        mensajeError = "El número de tarjeta es inválido."
                        return@Button
                    }

                    // 🚀 AHORA SÍ, VAMOS A MERCADO PAGO
                    procesando = true
                    scope.launch {
                        // Extraemos el año "28" -> 2028
                        val anioStr = vencimiento.substring(2, 4)
                        val anioCompleto = ("20" + anioStr).toIntOrNull() ?: 0

                        val tokenMP = MercadoPagoRepository.generarTokenReal(
                            numero = numTarjeta,
                            mes = mes,
                            año = anioCompleto,
                            cvv = cvv,
                            titular = nombreTitular
                        )

                        if (tokenMP != null) {
                            // 🚀 2. MAGIA NUEVA: Mandamos el token a la nube para hacerlo permanente
                            val auth = dev.gitlive.firebase.Firebase.auth
                            val emailCliente = auth.currentUser?.email ?: "compras@yendo.mx"

                            val (cardIdPermanente, issuerIdPermanente) = com.yendo.mx.repository.PagosRepository.guardarTarjetaEnNube(
                                tokenCard = tokenMP,
                                email = emailCliente
                            )

                            if (cardIdPermanente != null) {
                                // ÉXITO TOTAL: La tarjeta ya está guardada en el Customer de MP
                                val terminacion = numTarjeta.takeLast(4)
                                val marca = when {
                                    numTarjeta.startsWith("4") -> "Visa"
                                    numTarjeta.startsWith("5") -> "Mastercard"
                                    numTarjeta.startsWith("3") -> "Amex"
                                    else -> "Tarjeta"
                                }

                                onGuardar(
                                    TarjetaGuardada(
                                        id = "",
                                        terminacion = terminacion,
                                        titular = nombreTitular,
                                        marca = marca,
                                        tokenPago = cardIdPermanente, // 🚀 GUARDAMOS EL ID PERMANENTE
                                        issuerId = issuerIdPermanente ?: "" // 🚀 GUARDAMOS EL BANCO
                                    )
                                )
                                // No hace falta poner procesando = false porque onGuardar cierra el diálogo
                            } else {
                                // ERROR: Mercado pago rechazó asociar la tarjeta (ej. intentó usar una que ya no sirve)
                                mensajeError = "No se pudo asociar la tarjeta. Verifica que sea válida."
                                procesando = false
                            }

                        } else {
                            // ERROR: El banco o MP la rechazó al intentar crear el token
                            mensajeError = "Mercado Pago rechazó la tarjeta. Verifica tus datos."
                            procesando = false
                        }
                    }
                },
                enabled = !procesando, // 🚀 BLOQUEA EL BOTÓN ANTI-DOBLE CLIC
                colors = ButtonDefaults.buttonColors(
                    containerColor = RosaYendo,
                    disabledContainerColor = RosaYendo.copy(alpha = 0.5f)
                ),
                shape = RoundedCornerShape(10.dp)
            ) {
                Text(if (procesando) "Procesando..." else "Guardar Tarjeta", color = Color.White)
            }
        },
        dismissButton = {
            if (!procesando) {
                TextButton(onClick = onDismiss) { Text("Cancelar", color = colorTextoSecundario) }
            }
        }
    )
}

// ==========================================
// 🚀 TRANSFORMACIONES VISUALES
// ==========================================

class FormatoTarjetaVisual : VisualTransformation {
    override fun filter(text: AnnotatedString): TransformedText {
        val recortado = text.text.take(16)
        var salida = ""
        for (i in recortado.indices) {
            salida += recortado[i]
            if (i % 4 == 3 && i != 15) salida += " "
        }
        val offsetMapping = object : OffsetMapping {
            override fun originalToTransformed(offset: Int): Int {
                if (offset <= 3) return offset
                if (offset <= 7) return offset + 1
                if (offset <= 11) return offset + 2
                if (offset <= 16) return offset + 3
                return 19
            }
            override fun transformedToOriginal(offset: Int): Int {
                if (offset <= 4) return offset
                if (offset <= 9) return offset - 1
                if (offset <= 14) return offset - 2
                if (offset <= 19) return offset - 3
                return 16
            }
        }
        return TransformedText(AnnotatedString(salida), offsetMapping)
    }
}

class FormatoFechaVisual : VisualTransformation {
    override fun filter(text: AnnotatedString): TransformedText {
        val digits = text.text.filter { it.isDigit() }.take(4)
        var formatted = ""
        for (i in digits.indices) {
            formatted += digits[i]
            if (i == 1 && digits.length > 2) formatted += "/"
        }

        val offsetMapping = object : OffsetMapping {
            override fun originalToTransformed(offset: Int): Int {
                if (offset <= 1) return offset
                if (offset == 2) return if (digits.length > 2) 3 else 2
                return offset + 1
            }

            override fun transformedToOriginal(offset: Int): Int {
                if (offset <= 2) return offset
                if (offset == 3) return 2
                return offset - 1
            }
        }
        return TransformedText(AnnotatedString(formatted), offsetMapping)
    }
}